// Generic error message handler
export const getErrorMessage = (error: any): string => {
  if (!error) {
    return "An unknown error occurred. Please try again later.";
  }

  // Try to get error message from API response
  const errorResponse = error?.response?.data?.message || null;
  const errorData = error?.response?.data?.error?.message || null;
  const errorMessage = error?.message || null;

  // Return API error message if available
  if (errorResponse) {
    return errorResponse;
  }

  if (errorData) {
    return errorData;
  }

  if (errorMessage) {
    return errorMessage;
  }

  return "An unknown error occurred. Please try again later.";
};

// Legacy function name for backward compatibility (deprecated)
export const firebaseErrorMessage = getErrorMessage;

/**
 * Returns a user-friendly message for coupon/promo code errors.
 * Maps API messages to "This code has expired" or "Invalid promo code" where appropriate.
 */
export const getCouponErrorMessage = (
  error: any,
  fallbackMessage?: string,
): string => {
  const msg =
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.error ||
    (typeof error?.message === "string" ? error.message : null);
  const lower = msg ? String(msg).toLowerCase() : "";
  if (lower.includes("expired")) {
    return "This code has expired.";
  }
  if (
    lower.includes("invalid") ||
    lower.includes("not found") ||
    lower.includes("invalid promo") ||
    lower.includes("invalid coupon") ||
    lower.includes("does not exist")
  ) {
    return "Invalid promo code.";
  }
  if (msg && msg.trim()) {
    return msg.trim();
  }
  return fallbackMessage ?? "Invalid promo code. Please check and try again.";
};

export const errorMessages: Record<string, string> = {
  "email-already-in-use":
    "The email address is already in use by another account.",
  "invalid-email": "Invalid email address. Please check the format.",
  "weak-password": "The password is too weak. Please choose a stronger one.",
  "network-request-failed":
    "Internet connection failure. Kindly check your internet and try again.",
  "too-many-requests": "Too many requests, kindly try again in 10 minutes.",
  "user-not-found": "Account not found. Kindly register.",
  "wrong-password": "The password is incorrect. Please try again",
  ERR_NETWORK:
    "Internet connection failure - Kindly check your internet and try again.",
  "auth/phone-number-already-exists":
    "The provided phone number is already in use by an existing user. Each user must have a unique phone number.",
  "auth/too-many-requests":
    "The number of requests exceeds the maximum allowed. Kindly try again later.",
  "auth/code-expired":
    "The SMS code has expired. Please re-send the verification code to try again.",
  "auth/invalid-phone-number":
    "The provided phone number is invalid. It must be a 10 digit and a Ghana carrier number.",
  "auth/billing-not-enabled": "",
  "auth/internal-error": "",
  "auth/admin-restricted-operation": "Admin only operation",

  "auth/captcha-check-failed": "ReCaptcha check failed. Try again.",
  "auth/cors-unsupported": "This browser is not supported.",
  "auth/credential-already-in-use": "Credential already in use",
  "auth/custom-token-mismatch": "Credential mismatch",
  "auth/requires-recent-login": "Credential too old, login again",
  "auth/email-change-needs-verification": "Email change needs verification",
  "auth/expired-action-code": "Expired action code",
  "auth/cancelled-popup-request": "Expired popup request",
  "auth/invalid-user-token": "Invalid Auth",
  "auth/invalid-auth-event": "Invalid auth event",
  "auth/invalid-verification-code":
    "The verification code is not correct or expired.",
  "auth/invalid-custom-token": "Invalid custom token",
  "auth/invalid-credential": "Invalid credential.",
  "auth/invalid-action-code": "Invalid OOB code",
  "auth/unauthorized-domain": "Invalid origin. Unauthorized domain.",
  "auth/invalid-persistence-type": "Invalid persistence",
  "auth/invalid-verification-id": "Invalid session info",
  "auth/invalid-tenant-id": "Invalid tenant ID",
  "auth/missing-verification-code": "Missing code",
  "auth/missing-phone-number": "Missing phone number",
  "auth/account-exists-with-different-credential":
    "Account exists with different credential.",
  "auth/null-user": "Null user",
  "auth/operation-not-allowed": "Operation not allowed",
  "auth/popup-blocked": "Popup blocked",
  "auth/popup-closed-by-user": "Popup closed by user",
  "auth/quota-exceeded":
    "You have exceeded the allowed number of requests. Try again later.",

  "auth/redirect-cancelled-by-user": "Redirect cancelled by user",
  "auth/redirect-operation-pending": "Redirect operation pending",
  "auth/rejected-credential": "Rejected credential",
  "auth/second-factor-already-in-use": "Second factor already enrolled",
  "auth/maximum-second-factor-count-exceeded": "Second factor limit exceeded",
  "auth/tenant-id-mismatch": "Tenant ID mismatch",
  "auth/timeout": "Timeout",
  "auth/user-token-expired": "Token expired",
  "auth/unverified-email": "Unverified email",
  "auth/user-cancelled": "User cancelled",

  "auth/user-disabled": "Your account has been disabled. Contact support.",
  "auth/user-mismatch": "User mismatch",
  "auth/user-signed-out": "User signed out",
  // "auth/already-initialized": "Already initialized",
  "auth/recaptcha-not-enabled": "Recaptcha not enabled",

  "auth/missing-recaptcha-token": "Missing Recaptcha token",
  "auth/invalid-recaptcha-token":
    "Recaptcha verification failed. Please complete the challenge correctly.",
  "auth/invalid-recaptcha-action":
    "Recaptcha verification failed. Please complete the challenge correctly.",
  "auth/missing-recaptcha-version":
    "Recaptcha verification failed. Please complete the challenge correctly.",
  "auth/invalid-recaptcha-version":
    "Recaptcha verification failed. Please complete the challenge correctly.",

  "captcha-check-failed":
    "The reCAPTCHA response token provided is either invalid, expired, already used or the domain associated with it does not match the list of whitelisted domains.",
  "code-expired":
    "The SMS code has expired. Please re-send the verification code to try again.",
  // "cordova-not-ready": "Cordova framework is not ready.",
  "cors-unsupported": "This browser is not supported.",
  "credential-already-in-use":
    "This credential is already associated with a different user account.",
  "custom-token-mismatch":
    "The custom token corresponds to a different audience.",
  "requires-recent-login":
    "This operation is sensitive and requires recent authentication. Log in again before retrying this request.",
  // "dynamic-link-not-activated": "Please activate Dynamic Links in the Firebase Console and agree to the terms and conditions.",
  "email-change-needs-verification":
    "Multi-factor users must always have a verified email.",
  "expired-action-code": "The action code has expired. ",
  "cancelled-popup-request":
    "This operation has been cancelled due to another conflicting popup being opened.",
  "internal-error": "An internal error has occurred.",
  "invalid-app-credential":
    "The phone verification request contains an invalid application verifier. The reCAPTCHA token response is either invalid or expired.",
  // "invalid-app-id": "The mobile app identifier is not registed for the current project.",
  // "invalid-user-token": "This user's credential isn't valid for this project. This can happen if the user's token has been tampered with, or if the user isn't for the project associated with this API key.",
  // "invalid-auth-event": "An internal error has occurred.",
  "invalid-verification-code":
    "The SMS verification code used to create the phone auth credential is invalid. Please resend the verification code sms and be sure use the verification code received.",
  // "invalid-continue-uri": "The continue URL provided in the request is invalid.",
  // "invalid-cordova-configuration": "The following Cordova plugins must be installed to enable OAuth sign-in: cordova-plugin-buildinfo, cordova-universal-links-plugin, cordova-plugin-browsertab, cordova-plugin-inappbrowser and cordova-plugin-customurlscheme.",
  // "invalid-custom-token": "The custom token format is incorrect. Please check the documentation.",
  // "invalid-dynamic-link-domain": "The provided dynamic link domain is not configured or authorized for the current project.",
  // "invalid-api-key": "Your API key is invalid, please check you have copied it correctly.",
  // "invalid-cert-hash": "The SHA-1 certificate hash provided is invalid.",
  "invalid-credential":
    "The supplied auth credential is malformed or has expired.",
  // "invalid-message-payload": "The email template corresponding to this action contains invalid characters in its message. Please fix by going to the Auth email templates section in the Firebase Console.",
  // "invalid-multi-factor-session": "The request does not contain a valid proof of first factor successful sign-in.",
  // "invalid-oauth-provider": "EmailAuthProvider is not supported for this operation. This operation only supports OAuth providers.",
  // "invalid-oauth-client-id": "The OAuth client ID provided is either invalid or does not match the specified API key.",
  // "unauthorized-domain": "This domain is not authorized for OAuth operations for your Firebase project. Edit the list of authorized domains from the Firebase console.",
  "invalid-action-code":
    "The action code is invalid. This can happen if the code is malformed, expired, or has already been used.",
  // "invalid-persistence-type": "The specified persistence type is invalid. It can only be local, session or none.",
  // "invalid-phone-number": "The format of the phone number provided is incorrect. Please enter the phone number in a format that can be parsed into E.164 format. E.164 phone numbers are written in the format [+][country code][subscriber number including area code].",
  // "invalid-provider-id": "The specified provider ID is invalid.",
  // "invalid-recipient-email": "The email corresponding to this action failed to send as the provided recipient email address is invalid.",
  // "invalid-sender": "The email template corresponding to this action contains an invalid sender email or name. Please fix by going to the Auth email templates section in the Firebase Console.",
  "invalid-verification-id":
    "The verification ID used to create the phone auth credential is invalid.",
  // "invalid-tenant-id": "The Auth instance's tenant ID is invalid.",
  // "multi-factor-info-not-found": "The user does not have a second factor matching the identifier provided.",
  // "multi-factor-auth-required": "Proof of ownership of a second factor is required to complete sign-in.",
  // "missing-android-pkg-name": "An Android Package Name must be provided if the Android App is required to be installed.",
  // "auth-domain-config-required": "Be sure to include authDomain when calling firebase.initializeApp(), by following the instructions in the Firebase console.",
  "missing-app-credential":
    "The phone verification request is missing an application verifier assertion. A reCAPTCHA response token needs to be provided.",
  "missing-verification-code":
    "The phone auth credential was created with an empty SMS verification code.",
  // "missing-continue-uri": "A continue URL must be provided in the request.",
  // "missing-iframe-start": "An internal error has occurred.",
  // "missing-ios-bundle-id": "An iOS Bundle ID must be provided if an App Store ID is provided.",
  // "missing-multi-factor-info": "No second factor identifier is provided.",
  // "missing-multi-factor-session": "The request is missing proof of first factor successful sign-in.",
  // "missing-or-invalid-nonce": "The request does not contain a valid nonce. This can occur if the SHA-256 hash of the provided raw nonce does not match the hashed nonce in the ID token payload.",
  "missing-phone-number": "To send verification codes, provide a phone number.",
  // "missing-verification-id": "The phone auth credential was created with an empty verification ID.",
  // "app-deleted": "This instance of FirebaseApp has been deleted.",
  "account-exists-with-different-credential":
    "An account already exists with the same email address but different sign-in credentials. Sign in using a provider associated with this email address.",
  "no-auth-event": "An internal error has occurred.",
  "no-such-provider":
    "User was not linked to an account with the given provider.",
  // "null-user": "A null user object was provided as the argument for an operation which requires a non-null user object.",
  // "operation-not-allowed": "The given sign-in provider is disabled for this Firebase project. Enable it in the Firebase console, under the sign-in method tab of the Auth section.",
  // "operation-not-supported-in-this-environment": 'This operation is not supported in the environment this application is running on. "location.protocol" must be http, https or chrome-extension and web storage must be enabled.',
  "popup-blocked":
    "Unable to establish a connection with the popup. It may have been blocked by the browser.",
  "popup-closed-by-user":
    "The popup has been closed by the user before finalizing the operation.",
  "provider-already-linked":
    "User can only be linked to one identity for the given provider.",
  "quota-exceeded": "The project's quota for this operation has been exceeded.",
  // "redirect-cancelled-by-user": "The redirect operation has been cancelled by the user before finalizing.",
  // "redirect-operation-pending": "A redirect sign-in operation is already pending.",
  "rejected-credential":
    "The request contains malformed or mismatching credentials.",
  "second-factor-already-in-use":
    "The second factor is already enrolled on this account.",
  "maximum-second-factor-count-exceeded":
    "The maximum allowed number of second factors on a user has been exceeded.",
  // "tenant-id-mismatch": "The provided tenant ID does not match the Auth instance's tenant ID",
  timeout: "The operation has timed out.",
  "user-token-expired":
    "The user's credential is no longer valid. The user must sign in again.",
  // "unauthorized-continue-uri": "The domain of the continue URL is not whitelisted.  Please whitelist the domain in the Firebase console.",
  // "unsupported-first-factor": "Enrolling a second factor or signing in with a multi-factor account requires sign-in with a supported first factor.",
  // "unsupported-persistence-type": "The current environment does not support the specified persistence type.",
  // "unsupported-tenant-operation": "This operation is not supported in a multi-tenant context.",
  "unverified-email": "The operation requires a verified email.",
  "user-cancelled":
    "The user did not grant your application the permissions it requested.",
  "user-disabled":
    "The user account has been disabled by an administrator. Contact support.",
  "user-mismatch":
    "The supplied credentials do not correspond to the previously signed in user.",
  "user-signed-out": "",
  "web-storage-unsupported":
    "This browser is not supported or 3rd party cookies and data may be disabled.",
};

export function convertToInternationalFormat(phoneNumber: string): string {
  return "233" + phoneNumber.slice(1);
}
export function convertToLocalFormat(phoneNumber: string): string {
  // Check if the number starts with "233"
  if (phoneNumber.startsWith("233")) {
    // Remove the "233" prefix
    phoneNumber = phoneNumber.slice(3);
    // Add "0" to the front
    phoneNumber = "0" + phoneNumber;
  }

  if (phoneNumber.startsWith("+233")) {
    // Remove the "233" prefix
    phoneNumber = phoneNumber.slice(4);
    // Add "0" to the front
    phoneNumber = "0" + phoneNumber;
  }

  return phoneNumber;
}

export function checkAppointmentStatus(
  date: string,
  startTime: string,
  endTime: string,
): string {
  const [day, month, year] = date.split("-").map(Number);
  const [startHour, startMinute] = startTime
    .slice(0, -2)
    .split(":")
    .map(Number);
  const [endHour, endMinute] = endTime.slice(0, -2).split(":").map(Number);
  const isStartPM = startTime.slice(-2).toLowerCase() === "pm";
  const isEndPM = endTime.slice(-2).toLowerCase() === "pm";
  let adjustedStartHour = startHour;
  let adjustedEndHour = endHour;

  if (isStartPM && startHour !== 12) {
    adjustedStartHour += 12;
  } else if (!isStartPM && startHour === 12) {
    adjustedStartHour = 0;
  }

  if (isEndPM && endHour !== 12) {
    adjustedEndHour += 12;
  } else if (!isEndPM && endHour === 12) {
    adjustedEndHour = 0;
  }

  // Appointment times represent Ghana (Africa/Accra) local time, which is a
  // fixed UTC+0 offset with no DST - so building these as UTC directly (not
  // the device's own local timezone) gives the correct absolute instant
  // regardless of where the patient's/doctor's device physically is.
  const appointmentDateTime = new Date(
    Date.UTC(year, month - 1, day, adjustedStartHour, startMinute)
  );
  const endDateTime = new Date(
    Date.UTC(year, month - 1, day, adjustedEndHour, endMinute)
  );
  const currentDateTime = new Date();

  if (currentDateTime.getTime() > endDateTime.getTime()) {
    return "passed";
  } else if (
    currentDateTime.getTime() > appointmentDateTime.getTime() &&
    currentDateTime.getTime() < endDateTime.getTime()
  ) {
    return "within-time";
  } else {
    return "waiting";
  }
}

export function schedulePassed(
  date: string,
  startTime: string,
  endTime?: string,
): boolean {
  const [day, month, year] = date.split("-").map(Number);
  const [startHour, startMinute] = startTime
    .slice(0, -2)
    .split(":")
    .map(Number);
  const isStartPM = startTime.slice(-2).toLowerCase() === "pm";
  let adjustedStartHour = startHour;

  if (isStartPM && startHour !== 12) {
    adjustedStartHour += 12;
  } else if (!isStartPM && startHour === 12) {
    adjustedStartHour = 0;
  }

  // See checkAppointmentStatus above - anchor to Ghana time (UTC+0) rather
  // than the device's own timezone.
  const appointmentDateTime = new Date(
    Date.UTC(year, month - 1, day, adjustedStartHour, startMinute)
  );
  const currentDateTime = new Date();
  if (currentDateTime.getTime() > appointmentDateTime.getTime()) {
    return true;
  }
  return false;
}

// Format time as MM:SS
export const formatTime = (seconds: any) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};
